/**
 * 内容语言: 决定页面展示英文还是中文内容.
 *
 * 规则(与产品约定一致):
 * - english: 全部页面只看英文;
 * - chinese: 全部页面只看中文(缺译文时回退英文);
 * - bilingual: 仅跟读页同时显示中英, 其他页面按英文展示。
 *
 * 语言值来自当前登录用户 (`users.language`), 未登录或未设置时默认双语。
 */

import { coverSrc } from './cover'
import { authRef } from '../stores/auth'
import type { ContentLanguage, Course } from '../types/api'

export const DEFAULT_CONTENT_LANGUAGE: ContentLanguage = 'bilingual'

/** 设置项完整文案 */
export const CONTENT_LANGUAGE_LABELS: Record<ContentLanguage, string> = {
  english: '英文',
  chinese: '中文',
  bilingual: '双语',
}

/** 把接口返回值收敛到合法语言, 未知值按默认双语处理 */
export function normalizeLanguage(value: string | null | undefined): ContentLanguage {
  if (value === 'english' || value === 'chinese' || value === 'bilingual') return value
  return DEFAULT_CONTENT_LANGUAGE
}

/** 当前用户的内容语言 (模板 getter 里读取, 会随 store 响应式更新) */
export function currentLanguage(): ContentLanguage {
  return normalizeLanguage(authRef()?.user?.language)
}

/** 列表 / 卡片页实际使用的语言: 双语模式回退英文 */
export function listLanguage(): 'english' | 'chinese' {
  return currentLanguage() === 'chinese' ? 'chinese' : 'english'
}

/**
 * 资源卡片 / 详情的可读字段选择.
 *
 * 做成 mixin 展开进页面组件: { ...courseContent(), ... }。
 * 方法里读 `this.listLanguage`, 因此语言切换后模板会自动重渲染。
 */
export function courseContent() {
  return {
    /** 资源标题: 中文模式取译文, 缺失时回退英文 (直接读 store, 语言切换会触发重渲染) */
    titleOf(course: Pick<Course, 'title' | 'title_translation'>): string {
      if (listLanguage() === 'chinese') return course.title_translation || course.title
      return course.title
    },

    /** 资源简介: 中文模式取译文, 缺失时回退英文 */
    descriptionOf(course: Pick<Course, 'description' | 'description_translation'>): string {
      if (listLanguage() === 'chinese') {
        return course.description_translation || course.description
      }
      return course.description
    },

    /** 封面同源代理地址 (7 天浏览器缓存), 列表页共用 */
    coverSrc,
  }
}
